'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';
import { Dialog } from '@frameio/vapor';
import {
  type ExportDialogState,
  useExportDialogState,
} from '../../shared/hooks/useExportDialogState';
import { CompositionExportAll } from '../components/CompositionExport/CompositionExportAll';
import { UIBlock } from '../components/CompositionExport/UIBlock';
import { useExportRenderQueueMutation } from '../gql';

export type PendingExport = {
  jobId: string;
  assetFolderId: string;
  projectId: string | null | undefined;
  compositionRenderingCount: number;
  exportMarkersAsCommentsEnabled: boolean;
  onAfterExport: () => Promise<void>;
};

const CompositionExportContext = createContext<
  ExportDialogState & {
    getJobSettings: (jobId: string) => JobSettings | undefined;
    setRenderingCount: (count: number) => void;
    isRendering: boolean;
  }
>({
  open: () => undefined,
  close: () => undefined,
  isOpen: false,
  config: null,
  getJobSettings: () => undefined,
  setRenderingCount: () => undefined,
  isRendering: false,
});

type JobSettings = {
  folderId: string;
  projectId?: string;
  exportMarkersAsCommentsEnabled: boolean;
};

type JobSettingsMap = Record<string, JobSettings>;

export const CompositionExportContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const dialogState = useExportDialogState();
  const [pendingExport, setPendingExport] = useState<PendingExport | null>(
    null
  );
  const [renderingCount, setRenderingCount] = useState(0);
  const [exportRenderQueue, { loading: isRenderingQueue }] =
    useExportRenderQueueMutation({
      context: { resolveWithUXP: true },
    });

  const jobSettingsRef = useRef<JobSettingsMap>({});
  const setJobSettings = useCallback((jobId: string, settings: JobSettings) => {
    jobSettingsRef.current[jobId] = settings;
  }, []);

  const getJobSettings = useCallback(
    (jobId: string) => jobSettingsRef.current[jobId],
    []
  );

  useEffect(() => {
    if (dialogState.isOpen || !pendingExport) return;

    const { jobId, onAfterExport } = pendingExport;
    setPendingExport(null);
    exportRenderQueue({ variables: { jobId } }).then(onAfterExport);
  }, [dialogState.isOpen, pendingExport, exportRenderQueue]);

  const handleScheduleExport = useCallback(
    (params: PendingExport) => {
      setJobSettings(params.jobId, {
        folderId: params.assetFolderId,
        projectId: params.projectId ?? undefined,
        exportMarkersAsCommentsEnabled: params.exportMarkersAsCommentsEnabled,
      });
      setRenderingCount(params.compositionRenderingCount);
      setPendingExport(params);
      dialogState.close();
    },
    [dialogState, setJobSettings, setRenderingCount]
  );

  const isRendering =
    pendingExport !== null || isRenderingQueue || renderingCount > 0;

  const value = useMemo(
    () => ({ ...dialogState, getJobSettings, setRenderingCount, isRendering }),
    [dialogState, getJobSettings, setRenderingCount, isRendering]
  );

  return (
    <CompositionExportContext.Provider value={value}>
      {children}
      {isRendering && <UIBlock renderCount={renderingCount} />}
      <Dialog
        isShowing={dialogState.isOpen}
        onRequestClose={dialogState.close}
        style={{ width: '415px' }}
      >
        <CompositionExportAll
          onCancel={dialogState.close}
          onScheduleExport={handleScheduleExport}
          overrideFolderId={dialogState.config?.overrideFolderId ?? undefined}
        />
      </Dialog>
    </CompositionExportContext.Provider>
  );
};

export const useOpenCompositionExportDialog = () =>
  useContextSelector(CompositionExportContext, (context) => context.open);

export const useCloseCompositionExportDialog = () =>
  useContextSelector(CompositionExportContext, (context) => context.close);

export const useIsCompositionExportDialogOpen = () =>
  useContextSelector(CompositionExportContext, (context) => context.isOpen);

export const useCompositionExportDialogConfig = () =>
  useContextSelector(CompositionExportContext, (context) => context.config);

export const useGetCompositionExportJobSettings = () =>
  useContextSelector(CompositionExportContext, (ctx) => ctx.getJobSettings);

export const useSetRenderingCount = () =>
  useContextSelector(CompositionExportContext, (ctx) => ctx.setRenderingCount);

export const useIsCompositionRendering = () =>
  useContextSelector(CompositionExportContext, (ctx) => ctx.isRendering);
